import React, { memo, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box, FormField, Label, Select, ShadowOverlay, Tooltip, getPaletteColor } from 'pcln-design-system';
import { Warning } from 'pcln-icons';
import generateTimeOptions from '../utils/generateTimeOptions';
const FormFieldZIndex = /*#__PURE__*/styled(FormField).withConfig({
  displayName: "TimePicker__FormFieldZIndex",
  componentId: "sc-1chm2ig-0"
})(["position:relative;z-index:", ";"], props => props.zIndex);
const StyledLabel = /*#__PURE__*/styled(Label).withConfig({
  displayName: "TimePicker__StyledLabel",
  componentId: "sc-1chm2ig-1"
})(["z-index:auto;"]);
const StyledSelect = /*#__PURE__*/styled(Select).withConfig({
  displayName: "TimePicker__StyledSelect",
  componentId: "sc-1chm2ig-2"
})(["&&&{background-color:", ";height:", ";}", "}"], getPaletteColor('background.lightest'), props => props.height, props => props.hideChevron && `
      & + svg {
        display: none;
      }
    `);
/** @public */
function TimePicker({
  errorMessage,
  hasShadowEffect,
  height,
  hideTooltip,
  label,
  value = '12:00',
  zIndex = 2,
  onClose,
  onChange,
  onOpen,
  ...props
}) {
  const [isOpen, setOpen] = useState(false);
  const handleChange = useCallback(evt => {
    setOpen(false);
    onClose?.();
    onChange?.(evt);
  }, []);
  const handleOpen = useCallback(() => {
    setOpen(true);
    onOpen?.();
  }, [onOpen]);
  const handleClose = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose]);
  const handleKeyDown = useCallback(evt => {
    const key = evt.key;
    if (key === 'Escape') {
      setOpen(false);
      onClose?.();
    }
  }, []);
  const timeOptions = useMemo(() => generateTimeOptions(), []);
  const showError = errorMessage !== undefined && errorMessage !== '';
  const formFieldZIndex = isOpen ? zIndex : undefined;
  return React.createElement(React.Fragment, null, isOpen && hasShadowEffect && React.createElement(ShadowOverlay, {
    onClick: handleClose,
    zIndex: zIndex,
    "data-testid": 'time-picker-shadow-overlay'
  }), React.createElement(Box, null, React.createElement(FormFieldZIndex, {
    zIndex: formFieldZIndex
  }, label && React.createElement(StyledLabel, null, label), React.createElement(StyledSelect, {
    "aria-label": label,
    color: errorMessage && 'error',
    height: height,
    hideChevron: showError,
    value: value,
    onBlur: handleClose,
    onChange: handleChange,
    onClick: handleOpen,
    onFocus: handleOpen,
    onKeyDown: handleKeyDown,
    ...props
  }, timeOptions && timeOptions.map(time => React.createElement("option", {
    key: time.value,
    ...time
  }, time.label))), showError && React.createElement(Warning, {
    color: 'error',
    size: 24
  })), !isOpen && showError && !hideTooltip && React.createElement(Tooltip, {
    bottom: true,
    right: true,
    color: 'error'
  }, errorMessage)));
}
export default memo(TimePicker);